import { fromAddress, fromLatLng } from "react-geocode";

/**
 * Browser location helpers for "Nearby" searches and job requests.
 * Reverse geocoding uses the site's existing Google Maps key.
 */
export type Coordinates = { lat: number; lng: number };
export type Region = { country: string; state: string };
/** Country name → state names, as the provider app stores them on houses. */
export type RegionList = Record<string, string[]>;

const mapsKey = () => process.env.REACT_APP_GOOGLE_MAP_API_KEY;

export const currentPosition = () =>
  new Promise<Coordinates>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Location is not available in this browser"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      (error) => reject(new Error(error.message || "Location permission denied")),
      { timeout: 10_000, maximumAge: 300_000 }
    );
  });

type AddressComponent = { long_name: string; short_name: string; types: string[] };

/** Country and first-level region from a Google geocoding response. */
export function regionFromGeocode(results: { address_components?: AddressComponent[] }[]): Region | null {
  for (const result of results ?? []) {
    const parts = result.address_components ?? [];
    const country = parts.find((p) => p.types.includes("country"))?.long_name;
    const state = parts.find((p) => p.types.includes("administrative_area_level_1"))?.long_name;
    if (country) return { country, state: state ?? "" };
  }
  return null;
}

/** Lower case, no accents, no "State"/"Region"/"Province" suffix. */
export function normalizeRegionName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\b(state|region|province)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Maps a geocoded region onto the names houses are stored with (the API
 * compares them exactly). Google says "Oyo", the provider app stores
 * "Oyo State". An unknown state is dropped so the search still filters by
 * country.
 */
export function matchRegion(regions: RegionList, found: Region): Region | null {
  const country = Object.keys(regions).find(
    (name) => normalizeRegionName(name) === normalizeRegionName(found.country)
  );
  if (!country) return null;
  const target = normalizeRegionName(found.state);
  const state = target ? regions[country].find((name) => normalizeRegionName(name) === target) : undefined;
  return { country, state: state ?? "" };
}

/** The browser's position as a house search region. */
export async function currentHouseRegion(): Promise<Region> {
  const key = mapsKey();
  if (!key) throw new Error("Nearby search is not configured");
  const { lat, lng } = await currentPosition();
  const [{ results }, regions] = await Promise.all([
    fromLatLng(lat, lng, key),
    import("assets/house-regions.json").then((module) => module.default as RegionList),
  ]);
  const found = regionFromGeocode(results);
  const region = found && matchRegion(regions, found);
  if (!region) throw new Error("Could not work out your location");
  return region;
}

/** Coordinates for an address, used when location permission is refused. */
export async function coordinatesForAddress(address: string): Promise<Coordinates> {
  const key = mapsKey();
  if (!key) throw new Error("Address lookup is not configured");
  const { results } = await fromAddress(address, key);
  const location = results?.[0]?.geometry?.location;
  if (typeof location?.lat !== "number") throw new Error("Address not found");
  return { lat: location.lat, lng: location.lng };
}
